<template>
    <VJoyPanel v-if="formattedQuoteLines">
        <template #panel-body>
            <TDataTable :value="formattedQuoteLines" class="quotelines-table">
                <TColumn v-for="column of columns" :key="column">
                    <template #header>
                        <div class="quotelines-table_header / flex" :class="`column-${column}`">
                            <span>{{ $t(`projectclient.projectview.details.quote-lines.table.column.${column}`) }}</span>
                        </div>
                    </template>
                    <template #body="{data}">
                        <QuoteLineCell :column="column" :content="data[column]" />
                    </template>
                </TColumn>
            </TDataTable>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useFormat} from '#imports';
    import {createDataTableComponents} from '@malt/joy-entrypoint/src/vue/utils';
    import {VJoyPanel} from '@maltjoy/core-vue';
    import type {FormattedQuoteLine} from './quote-lines.types';
    import QuoteLineCell from './QuoteLineCell.vue';
    import {useProjectInformationStore} from '@/common/stores';

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const {formatAmount} = useFormat();

    const columns: (keyof FormattedQuoteLine)[] = ['title', 'amount', 'unitPrice', 'totalPrice'];

    const formattedQuoteLines = computed<FormattedQuoteLine[]>(() => {
        return (
            projectInformation.value.quoteLines?.map((quoteLine) => ({
                title: quoteLine.description,
                amount: quoteLine.quantity,
                unitPrice: formatAmount(projectInformation.value.contract.currency, quoteLine.unitPriceExcludingTaxes, {decimals: 2}),
                totalPrice: formatAmount(projectInformation.value.contract.currency, quoteLine.totalExcludingTaxes, {decimals: 2}),
            })) ?? []
        );
    });

    const {TColumn, TDataTable} = createDataTableComponents<FormattedQuoteLine>();
</script>

<style lang="scss" scoped>
    section.joy-panel--medium {
        padding: 0 !important;
        overflow: hidden;
    }

    .quotelines-table {
        :deep(tr:last-child > td) {
            border-bottom: none;
        }

        &_header {
            display: flex;
            font-weight: var(--joy-font-weight-bold);
            justify-content: flex-end;
            width: 100%;

            &.column-title {
                justify-content: flex-start;
            }
        }
    }
</style>
